import { Routes, Route } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { FloatingWhatsAppButton } from './components/ui/FloatingWhatsAppButton';
import { SkipLink } from './components/ui/SkipLink';
import { useTenant } from './hooks/useTenant';
import { dataLoader } from './services/dataLoader.service';

// Pages
import HomePage from './pages/HomePage';
import VentasPage from './pages/VentasPage';
import ArriendosPage from './pages/ArriendosPage';
import ServiciosPage from './pages/ServiciosPage';
import QuienesSomosPage from './pages/QuienesSomosPage';
import ContactoPage from './pages/ContactoPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import AccesibilidadPage from './pages/AccesibilidadPage';

// Loading Component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading application...</p>
      </div>
    </div>
  );
}

// Error Component
function ErrorScreen({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md px-4">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h2 className="text-xl font-semibold mb-2">Configuration Error</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function App() {
  const { config, loading, error, refetch } = useTenant();
  const [dataLoading, setDataLoading] = useState(false);
  const [appReady, setAppReady] = useState(false);

  // Update document metadata when config changes
  useEffect(() => {
    if (config) {
      // Update document title
      document.title = config.seo.title;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', config.seo.description);

      // Update favicon
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.setAttribute('rel', 'icon');
        document.head.appendChild(favicon);
      }
      favicon.href = config.favicon;

      // Update apple-touch-icon
      let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement('link');
        appleTouchIcon.setAttribute('rel', 'apple-touch-icon');
        document.head.appendChild(appleTouchIcon);
      }
      appleTouchIcon.href = `/assets/${config.id}/apple-touch-icon.png`;

      // Update PWA manifest
      let manifest = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
      if (!manifest) {
        manifest = document.createElement('link');
        manifest.setAttribute('rel', 'manifest');
        document.head.appendChild(manifest);
      }
      manifest.href = `/manifests/${config.id}.webmanifest`;

      // Update theme-color
      let themeColor = document.querySelector('meta[name="theme-color"]');
      if (!themeColor) {
        themeColor = document.createElement('meta');
        themeColor.setAttribute('name', 'theme-color');
        document.head.appendChild(themeColor);
      }
      themeColor.setAttribute('content', config.primaryColor);

      // Update apple-mobile-web-app-status-bar-style
      let appleStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (!appleStatusBar) {
        appleStatusBar = document.createElement('meta');
        appleStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
        document.head.appendChild(appleStatusBar);
      }
      appleStatusBar.setAttribute('content', config.theme?.mode === 'dark' ? 'black-translucent' : 'default');

      // Update apple-mobile-web-app-title
      let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (!appleTitle) {
        appleTitle = document.createElement('meta');
        appleTitle.setAttribute('name', 'apple-mobile-web-app-title');
        document.head.appendChild(appleTitle);
      }
      appleTitle.setAttribute('content', config.name);

      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', config.seo.keywords.join(', '));

      // Update OG tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', config.seo.title);

      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', config.seo.description);

      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute('content', config.seo.ogImage);
    }
  }, [config]);

  // Preload data when config is available
  useEffect(() => {
    if (config && !appReady) {
      setDataLoading(true);
      
      async function preloadData() {
        try {
          await dataLoader.preloadData(['/']);
        } catch (error) {
          console.error('Error preloading data:', error);
          // Don't fail the app if preloading fails
        } finally {
          setDataLoading(false);
          setAppReady(true);
        }
      }
      
      preloadData();
    }
  }, [config, appReady]);

  // Show loading screen while tenant is loading or data is preloading
  if (loading || (config && dataLoading)) {
    return <LoadingScreen />;
  }

  // Show error screen if we couldn't load config and it's a critical error
  if (!config && error && !error.includes('cached') && !error.includes('default')) {
    return <ErrorScreen error={error} onRetry={refetch} />;
  }

  // If we still don't have config, something is really wrong
  if (!config) {
    return (
      <ErrorScreen 
        error="Unable to load application configuration" 
        onRetry={refetch} 
      />
    );
  }

  return (
    <HelmetProvider>
      <div
        className="min-h-screen flex flex-col"
        style={{
          '--primary-color': config.primaryColor,
          '--secondary-color': config.secondaryColor,
          '--accent-color': config.accentColor,
        } as React.CSSProperties}
      >
        {/* Accessibility: Skip to main content link */}
        <SkipLink />

        {/* Error banner for non-critical errors */}
        {error && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
            <div className="flex items-center justify-between text-yellow-800 text-sm">
              <span>{error}</span>
              <button
                onClick={refetch}
                className="text-yellow-600 hover:text-yellow-800 underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        <Header />
        <main id="main-content" className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/ventas" element={<VentasPage />} />
            <Route path="/arriendos" element={<ArriendosPage />} />
            <Route path="/servicios" element={<ServiciosPage />} />
            <Route path="/quienes-somos" element={<QuienesSomosPage />} />
            <Route path="/contacto" element={<ContactoPage />} />
            {config.features.blog && (
              <>
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
              </>
            )}
            <Route path="/propiedad/:id" element={<PropertyDetailPage />} />
            <Route path="/accesibilidad" element={<AccesibilidadPage />} />
            {/* 404 - Catch all unmatched routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />

        {/* Floating WhatsApp Button - Available on all pages */}
        <FloatingWhatsAppButton />
      </div>
    </HelmetProvider>
  );
}

export default App;